import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { useEffect, useState } from "react";
import {
  FiDownload,
  FiFilter,
  FiRefreshCw,
  FiTrendingUp,
} from "react-icons/fi";
import { toast } from "react-toastify";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import SummaryApi from "../common";
import displayNPRCurrency from "../helpers/displayCurrency";
import { request } from "../helpers/request";

const AdminSalesReport = () => {
  const [data, setData] = useState({
    orders: [],
    periodStats: { totalRevenue: 0, totalOrders: 0, avgOrderValue: 0 },
    paymentStats: [],
    dailySales: [],
    categoryPerformance: [],
  });
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(
    moment().subtract(30, "days").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await request({
        url: `${SummaryApi.getSalesReport.url}?startDate=${startDate}&endDate=${endDate}`,
        method: SummaryApi.getSalesReport.method,
      });
      const resData = await response.json();

      if (resData.success) {
        setData(resData.data);
      } else {
        toast.error(resData.message || "Failed to fetch sales report");
      }
    } catch (error) {
      toast.error("An error occurred while fetching report");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchReport();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadPDF = () => {
    const doc = new jsPDF();
    const timestamp = moment().format("YYYY-MM-DD_HH-mm");

    doc.setFontSize(22);
    doc.setTextColor(220, 38, 38);
    doc.text("SHALIMAR GEARS", 105, 20, { align: "center" });

    doc.setFontSize(16);
    doc.setTextColor(30, 41, 59);
    doc.text("Advanced Sales Report", 105, 30, { align: "center" });

    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(
      `Period: ${moment(startDate).format("MMM D, YYYY")} - ${moment(endDate).format("MMM D, YYYY")}`,
      105,
      38,
      { align: "center" },
    );

    doc.autoTable({
      head: [["Category", "Revenue", "Units Sold", "Orders"]],
      body: data.categoryPerformance.map((cat) => [
        cat.category,
        `NPR ${cat.revenue.toLocaleString()}`,
        cat.unitsSold,
        cat.orderCount,
      ]),
      startY: 50,
      theme: "grid",
      headStyles: { fillColor: [220, 38, 38] },
    });

    doc.save(`Sales_Report_${timestamp}.pdf`);
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-3xl border border-surface-border shadow-sm">
      <div
        className={`w-12 h-12 rounded-2xl ${color} flex items-center justify-center text-white mb-4 shadow-lg`}
      >
        <Icon size={20} />
      </div>
      <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">
        {title}
      </p>
      <h3 className="text-2xl font-black text-primary tracking-tight">
        {value}
      </h3>
    </div>
  );

  return (
    <div className="space-y-8 pb-12">
      <div className="flex flex-col lg:flex-row justify-between lg:items-end gap-6">
        <div className="space-y-2">
          <h1 className="text-4xl font-black text-primary tracking-tight">
            Sales Intelligence
          </h1>
          <p className="text-gray-500 font-medium">
            Categorical profitability and automated reporting.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center bg-white rounded-2xl border border-surface-border p-1 shadow-sm">
            <input
              type="date"
              className="bg-transparent border-none outline-none text-xs font-bold p-2 text-primary"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <span className="text-gray-300 px-2">—</span>
            <input
              type="date"
              className="bg-transparent border-none outline-none text-xs font-bold p-2 text-primary"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <button
              onClick={fetchReport}
              className="ml-2 w-10 h-10 bg-primary text-white rounded-xl flex items-center justify-center"
            >
              <FiRefreshCw />
            </button>
          </div>
          <button
            onClick={downloadPDF}
            className="flex items-center gap-3 px-6 py-4 bg-accent text-white rounded-2xl font-bold shadow-xl shadow-accent/20"
          >
            <FiDownload /> Download Report
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Total Revenue"
          value={displayNPRCurrency(data.periodStats.totalRevenue)}
          icon={FiTrendingUp}
          color="bg-green-500"
        />
        <StatCard
          title="Orders"
          value={data.periodStats.totalOrders}
          icon={FiFilter}
          color="bg-blue-500"
        />
        <StatCard
          title="Avg Order"
          value={displayNPRCurrency(data.periodStats.avgOrderValue)}
          icon={FiTrendingUp}
          color="bg-purple-500"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Chart */}
        <div className="bg-white p-8 rounded-[2.5rem] border border-surface-border shadow-sm">
          <h3 className="text-xl font-black text-primary mb-8">
            Revenue Performance
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.dailySales}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#f1f5f9"
                />
                <XAxis
                  dataKey="_id"
                  tickFormatter={(val) => moment(val).format("MMM D")}
                  tick={{ fontSize: 10 }}
                />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#d12e2e"
                  fill="#d12e2e"
                  fillOpacity={0.1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Profitability Table */}
        <div className="bg-white rounded-[2.5rem] border border-surface-border shadow-sm overflow-hidden">
          <div className="p-8 border-b border-surface-border">
            <h3 className="text-xl font-black text-primary tracking-tight">
              Category Profitability
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-surface-muted/50">
                  <th className="px-8 py-4 text-[10px] font-black uppercase text-gray-400">
                    Category
                  </th>
                  <th className="px-8 py-4 text-[10px] font-black uppercase text-gray-400">
                    Revenue
                  </th>
                  <th className="px-8 py-4 text-[10px] font-black uppercase text-gray-400">
                    Units
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {data.categoryPerformance?.map((cat, idx) => (
                  <tr key={idx}>
                    <td className="px-8 py-4 font-bold text-primary capitalize">
                      {cat.category}
                    </td>
                    <td className="px-8 py-4 font-black">
                      {displayNPRCurrency(cat.revenue)}
                    </td>
                    <td className="px-8 py-4 text-gray-500">{cat.unitsSold}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Transactions */}
      <div className="bg-white rounded-[2.5rem] border border-surface-border shadow-sm overflow-hidden">
        <div className="p-8 border-b border-surface-border">
          <h3 className="text-xl font-black text-primary">
            Recent Transactions
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-surface-muted/50">
                <th className="px-8 py-4 text-[10px] font-black uppercase text-gray-400">
                  Date
                </th>
                <th className="px-8 py-4 text-[10px] font-black uppercase text-gray-400">
                  Customer
                </th>
                <th className="px-8 py-4 text-[10px] font-black uppercase text-gray-400 text-right">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {data.orders.map((order) => (
                <tr key={order._id}>
                  <td className="px-8 py-4 text-sm font-bold text-gray-500">
                    {moment(order.createdAt).format("MMM D")}
                  </td>
                  <td className="px-8 py-4 font-bold text-primary">
                    {order.userId?.name || "Guest"}
                  </td>
                  <td className="px-8 py-4 text-right font-black">
                    {displayNPRCurrency(order.totalAmount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminSalesReport;
